/**
 * The state for a list of campaigns.
 */
import axios from 'axios';

const state = () => ({
    campaigns: {},
    completionStatus: {
        completed: [],
        inProgress: []
    }
});

// mutations
const mutations = {
    setCampaigns(state, campaigns) {
        const campaignsList =  JSON.parse(JSON.stringify(campaigns));
        // Group the list of campaigns by membership status
        state.campaigns = campaignsList.reduce((groups, item) => {
            // Assume 'accepted' when memberStatus prop is not available
            // as is the case with campaigns where campaignOptIn FF is disabled on the app side.
            const status = 'memberStatus' in item ? item.memberStatus : 'accepted';
            if (!groups.hasOwnProperty(status)) {
                groups[status] = [];
            }
            groups[status].push(item);
            return groups;
        }, {});
    },
    /**
     * Sets the completion statuses for campaigns in the state.
     *
     * @param {object} state - The Vuex state object.
     * @param {object} completionStatus - The completion status object to set.
     */
    setCompletionStatuses(state, completionStatus) {
        const completionStatusList =  JSON.parse(JSON.stringify(completionStatus));
        Object.entries(completionStatusList).forEach(([campaignId, completionStatus]) => {
            if (completionStatus === null) {
                return;
            }
            const status = completionStatus ? 'completed' : 'inProgress';
            state.completionStatus[status].push(Number(campaignId));
        });
    }
};

// getters
const getters = {
    /**
     * Returns a flattened list of campaigns where the influencer is in the accepted/applied state
     * @param {Object} state Contains the current state of the campaignsList data
     * @returns {Array} The list of campaigns
     */
    activeAndPendingCampaigns: (state) => [...state.campaigns?.accepted || [], ...state.campaigns?.applied || []],
    /**
     * Retrieves the completed campaigns
     *
     * @param {object} state - The state object containing completion status and campaigns.
     * @returns {array} An array of completed campaigns.
     */
    completedCampaigns: (state) => state.completionStatus.completed.map((campaignId) => state.campaigns?.accepted?.find((campaign) => campaign.projectId === campaignId)),
    /**
     * Retrieves the in progress campaigns
     *
     * @param {object} state - The state object containing completion status and campaigns.
     * @returns {array} An array of in progress campaigns.
     */
    inProgressCampaigns: (state) => state.completionStatus.inProgress.map((campaignId) => state.campaigns?.accepted?.find((campaign) => campaign.projectId === campaignId))
};

// actions
const actions = {
    /**
     * Gets a all campaigns.
     * @param {object} context - The vuex context
     * @param {object} params - Extra URL params
     * @returns {Array} A list of campaigns
     */
    async getCampaigns({ commit }, { params = null }) {
        try {
            const apiUrl = `/campaigns/getByInf`;
            const options = {};
            if (params) {
                options.params = params;
            }

            const response = await axios.get(apiUrl, options);
            const data = response?.data ?? [];
            commit('setCampaigns', data);

            return data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Retrieves the completion status of campaigns.
     *
     * @param {object} commit - The Vuex commit function for updating state.
     * @param {object} options - Options object containing infUid and campaignIds.
     * @param {string} options.infUid - The infUid for identifying the user.
     * @param {array} options.campaignIds - An array of campaign IDs to check completion status for.
     * @throws {Error} Throws an error if required parameters are missing.
     */
    async getCampaignCompletionStatus({ commit }, { infUid, campaignIds }) {
        try {
            if (!infUid || !campaignIds) {
                throw new Error('cannot execute getCampaignCompletionStatus without the required parameters');
            }
            if (!campaignIds.length) {
                return [];
            }
            const apiUrl = `/drafts/areAllSubmissionsApproved`;
            const response = await axios.post(apiUrl, {
                infUid,
                campaignIds
            });
            const data = response?.data?.data ?? [];
            commit('setCompletionStatuses', data);
            return data;
        } catch (error) {
            throw error;
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
